import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useUserData } from "hooks/UserContext";
import { clientesApi } from "page/OrderResumePag/_services/api";
import { getCookie } from "lib/cookies";
import { getCustomerFromMemory, setCustomerInMemory } from "lib/sharedSessionMemory";

// SHA-256 hash function for verifying data integrity
async function sha256Hex(input) {
  const data = new TextEncoder().encode(input);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

const CHECKOUT_SALT = "z{*'";

export default function FlightTickets() {
  const [iframeUrl, setIframeUrl] = useState(null);
  const [error, setError] = useState(null);
  const { user, setUserData, setUser } = useUserData();
  const navigate = useNavigate();
  const iframeRef = useRef(null);

  useEffect(() => {
    try {
      if (!user || !user.balance) return;

      const channel = process.env.REACT_APP_DEFAULT_CHANNEL;
      const envisionUrl = process.env.REACT_APP_ENVISION_URL;
      const hasPendingBanner = !!sessionStorage.getItem('pending_banner_redirect');
      const fullUrl = `${envisionUrl}/?channel=${channel}&csrf=true${hasPendingBanner ? '&bannerRedirect=true' : ''}`;

      setIframeUrl(fullUrl);
    } catch (err) {
      console.error("Erro ao montar iframe:", err);
      setError("Erro interno ao carregar o conteúdo.");
    }
  }, [user?.uuid]);

  useEffect(() => {
    const sendAuthData = (source) => {
      const wlToken = getCookie('wlt');
      const customer = getCustomerFromMemory();
      const userData = customer?.data || null;
      const balance = user?.balance?.data ?? 0;

      if (!wlToken || !source) return;

      const payload = { token: wlToken, userData, balance };
      console.log('[FlightTickets] >>> Enviando AUTH_DATA', { userData: !!userData, balance });
      source.postMessage(
        { type: 'AUTH_DATA', payload },
        '*'
      );
    };

    const updateUserDataEvent = async () => {
      const userData = getCustomerFromMemory();

      const balance = await clientesApi.get(
        `customer/${userData.data.cpf}/balance`
      );

      if (userData) {
        userData.data.balance = {
          data: balance.data.data
        };
        setCustomerInMemory(userData);

        setUserData(userData);
        setUser(userData.data);
      }
    };

    const handleMessage = async (event) => {
      const data = event.data;

      if (data?.type === 'HANDSHAKE_START') {
        console.log('[FlightTickets] <<< Recebido HANDSHAKE_START');
        sendAuthData(event.source);
      }

      if (data?.type === 'HANDSHAKE_COMPLETE') {
        console.log('[FlightTickets] <<< Recebido HANDSHAKE_COMPLETE - Handshake finalizado');

        const pendingBanner = sessionStorage.getItem('pending_banner_redirect');
        if (pendingBanner) {
          try {
            const bannerData = JSON.parse(pendingBanner);
            if (bannerData.bannerType === 'passagem') {
              sessionStorage.removeItem('pending_banner_redirect');
              event.source.postMessage({
                type: 'REDIRECT_RESULT',
                payload: {
                  result_type: 'search_flight',
                  bannerData: bannerData.data,
                }
              }, '*');
              console.log('[FlightTickets] >>> Enviando REDIRECT_RESULT (flight)', bannerData.data);
            }
          } catch (err) {
            console.error('[FlightTickets] Erro ao processar banner redirect:', err);
            sessionStorage.removeItem('pending_banner_redirect');
          }
        }
      }

      if (data?.type === 'UPDATE_USER_DATA') {
        updateUserDataEvent();
      }

      if (data && data.type === 'ga4_event') {
        const ga4Payload = data.payload;
        if (window.dataLayer) {
          window.dataLayer.push(ga4Payload);
        }
      }

      // Check if this is a checkout message
      if (data?.message === 'start-checkout-aereo' && data?.payload) {
        try {
          const payload = data.payload;
          const receivedHash = data.hash;

          const computedHash = await sha256Hex(payload + CHECKOUT_SALT);

          if (receivedHash !== computedHash) {
            console.error('Hash mismatch - data integrity check failed');
            setError('Erro de validação dos dados do checkout.');
            return;
          }

          const encoder = new TextEncoder();
          const uint8Array = encoder.encode(payload);
          const encodedData = btoa(String.fromCharCode(...uint8Array));
          localStorage.setItem('envision', encodedData);

          navigate('/loja/orderresumepag/?checkout=aereo');
        } catch (err) {
          console.error('Error processing checkout message:', err);
          setError('Erro ao processar dados do checkout.');
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [navigate, user, setUserData, setUser]);

  if (error) {
    return (
      <div style={{ textAlign: "center", marginTop: "100px", color: "red" }}>
        {error}
      </div>
    );
  }

  if (!iframeUrl) {
    return <p style={{ textAlign: "center", marginTop: "100px" }}>Carregando...</p>;
  }

  return (
    <div style={{ width: "100%", height: "100vh" }}>
      <iframe
        ref={iframeRef}
        src={iframeUrl}
        title="Flight Tickets"
        style={{
          width: "100%",
          height: "100%",
          border: "none",
          overflow: "hidden",
          paddingTop: "16vh"
        }}
      />
    </div>
  );
}
